import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseTabNavigationOptions<T> {
    defaultTab: T;
    paramName?: string;
    onTabChange?: (tab: T, previousTab: T, isNavigatingViaHistory: boolean) => void;
}

export const useTabNavigation = <T extends string>({
    defaultTab,
    paramName = 'tab',
    onTabChange,
}: UseTabNavigationOptions<T>) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabFromUrl = searchParams.get(paramName) as T | null;
    const [currentTab, setCurrentTab] = useState<T>(tabFromUrl || defaultTab);
    const isNavigatingViaHistoryRef = useRef(false);

    useEffect(() => {
        const tabFromUrl = searchParams.get(paramName) as T | null;
        const targetTab = tabFromUrl || defaultTab;

        if (targetTab !== currentTab) {
            isNavigatingViaHistoryRef.current = true;
            const previousTab = currentTab;
            setCurrentTab(targetTab);
            onTabChange?.(targetTab, previousTab, true);

            setTimeout(() => {
                isNavigatingViaHistoryRef.current = false;
            }, 0);
        }
    }, [searchParams, currentTab, defaultTab, paramName, onTabChange]);

    const openTab = useCallback(
        (tab: T) => {
            const params = new URLSearchParams(searchParams.toString());
            const previousTab = currentTab;

            if (tab !== defaultTab) {
                params.set(paramName, tab);
            } else {
                params.delete(paramName);
            }

            setSearchParams(params, { replace: false });
            setCurrentTab(tab);
            onTabChange?.(tab, previousTab, false);
        },
        [searchParams, setSearchParams, defaultTab, paramName, currentTab, onTabChange],
    );

    return {
        currentTab,
        openTab,
    };
};
